import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ShotsContext } from '../contexts/shots/provider';
import { Shot, ShotFeed } from '../stores/reducers/shots';
import { GreenroomState } from '../stores/types/types';
import { LAYOUTS_BY_SIZE, VideoLayouts } from '../utils/video-layout';
import { OpenTokContext } from '../contexts/opentok/opentok-context';

const RESOLUTIONS_1080 = {
	HIGH: { height: 1080, width: 1920 },
	MEDIUM: { height: 540, width: 960 }, // 960x540
	LOW: { height: 270, width: 480 },
};

const RESOLUTIONS_720 = {
	HIGH: { height: 720, width: 1280 },
	MEDIUM: { height: 360, width: 640 },
	LOW: { height: 180, width: 320 },
};

enum RESOLUTIONS_ENUM {
	HIGH='HIGH',
	MEDIUM='MEDIUM',
	LOW='LOW',
}

const useResolutionUpdate = () => {
	const { shots } = useSelector((state: GreenroomState) => state.ShotsReducer);
	const { feedScaling } = useSelector((state: GreenroomState) => state.SettingsReducer);
	const { connectedPresenterVideos } = useSelector((state: GreenroomState) => state.VideoReducer);
	const { opentok } = useContext(OpenTokContext);
	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);

	const {
		currentShot: {
			shot_data: {
				order, shotFeeds, forcedLayout,
			},
		},
	} = useContext(ShotsContext);

	const determineResolution = (
		shotLayout: string,
		position: any,
		presenterProfile: ShotFeed,
		layoutSize: number |undefined,
	) => {
		// position can be either string or number
		const stringifiedPosition = position.toString();
		let thisShotShouldBe1080;
		switch (shotLayout) {
			case 'single cover':
			case 'single-padding cover':
			case 'picture-in-picture bottom left':
				thisShotShouldBe1080 = stringifiedPosition === '0';
				break;
			case 'double cover':
			case 'picture-in-picture bottom right':
			case 'picture-in-picture top left':
				thisShotShouldBe1080 = stringifiedPosition === '1';
				break;
			case 'vertical two cover':
				thisShotShouldBe1080 = true;
				break;
			default:
				thisShotShouldBe1080 = false;
		}
		if (thisShotShouldBe1080) {
			return RESOLUTIONS_ENUM.HIGH;// { height: 1080, width: 1920 };
		} if (presenterProfile.audioOnly) {
			return RESOLUTIONS_ENUM.LOW;// { height: 180, width: 320 };
		} if (layoutSize && layoutSize < 5) {
			return RESOLUTIONS_ENUM.MEDIUM;// { height: 720, width: 1280 };
		}
		return RESOLUTIONS_ENUM.LOW;// { height: 180, width: 320 }
	};

	const getStreamIdFromGrUser = (grUser: number) => connectedPresenterVideos.find(
		({ profile, isScreenShare }) => profile === grUser && !isScreenShare,
	)?.id ?? '';

	useEffect(() => {
		if (feedScaling && opentok && embed) {
			Object.entries(opentok?.subscribers).forEach(([, subscriber]) => {
				const RESOLUTIONS = subscriber.stream?.videoDimensions?.height === 720
					? RESOLUTIONS_720 : RESOLUTIONS_1080;
				subscriber.setPreferredResolution(RESOLUTIONS.HIGH);
			});
		}
	}, [connectedPresenterVideos]);

	useEffect(() => {
		if (feedScaling && opentok && !embed) {
			// update resolution for presenters in the next shot/previous shot
			if (!order && order !== 0) {
				return;
			}

			const thisAndNextShotPresenters:any = {};

			// get number of feeds for current and next shot
			const currentLayoutSize = LAYOUTS_BY_SIZE.find((config: VideoLayouts) => config.layouts.includes(forcedLayout ?? ''))?.presenters;
			const presentersInCurrentShot = shotFeeds.map((feed) => feed);
			presentersInCurrentShot?.forEach((presenterCurrent) => {
				if (presenterCurrent.presenter) {
					const inCurrentShotIndexPres = (
						shotFeeds.findIndex(
							(curFeed) => curFeed.presenter === presenterCurrent.presenter,
						)
					);
					const streamId = getStreamIdFromGrUser(presenterCurrent.presenter);
					const subscriber = opentok?.subscribers[streamId];
					if (streamId && subscriber) {
						const RESOLUTIONS = subscriber.stream?.videoDimensions?.height === 720
							? RESOLUTIONS_720 : RESOLUTIONS_1080;
						const shotResolution = RESOLUTIONS[determineResolution(
							forcedLayout,
							shotFeeds[inCurrentShotIndexPres].position,
							presenterCurrent,
							currentLayoutSize,
						)];

						thisAndNextShotPresenters[streamId] = { currentShotResolution: shotResolution };
					}
				}
			});

			const newNextShot: Shot | undefined = shots.find(
				(shot: Shot) => shot.shot_data.order === order + 1,
			);
			if (newNextShot) {
				const { forcedLayout: nForcedLayout, shotFeeds: nShotFeeds } = newNextShot.shot_data;
				const nextLayoutSize = LAYOUTS_BY_SIZE.find(
					(config: VideoLayouts) => config.layouts.includes(nForcedLayout ?? ''),
				)?.presenters;
				const presentersInNextShot = nShotFeeds.reduce((accumulator: ShotFeed[], currentFeed: ShotFeed) => {
					if (currentFeed.presenter) {
						accumulator.push(currentFeed);
					}
					return accumulator;
				}, []);
				presentersInNextShot?.forEach((presenterNext) => {
					if (presenterNext.presenter) {
						const inNextShotIndexPres = (
							newNextShot?.shot_data.shotFeeds.findIndex(
								(curFeed) => curFeed.presenter === presenterNext.presenter,
							)
						);
						const streamId = getStreamIdFromGrUser(presenterNext.presenter);
						const subscriber = opentok?.subscribers[streamId];
						if (streamId && subscriber) {
							const RESOLUTIONS = subscriber.stream?.videoDimensions?.height === 720
								? RESOLUTIONS_720 : RESOLUTIONS_1080;
							const shotResolution = RESOLUTIONS[determineResolution(
								nForcedLayout,
								nShotFeeds[inNextShotIndexPres].position,
								presenterNext,
								nextLayoutSize,
							)];

							thisAndNextShotPresenters[streamId] = {
								...thisAndNextShotPresenters[streamId],
								nextShotResolution: shotResolution,
							};
						}
					}
				});
			}
			const doNotSetAsLowestResolution = Object.keys(thisAndNextShotPresenters);
			const allSubscriberKeys = Object.keys(opentok?.subscribers);
			const keysToSetAsLowestResolution = allSubscriberKeys.filter(
				(el) => !doNotSetAsLowestResolution.includes(el),
			);
			Object.entries(thisAndNextShotPresenters).forEach(([key, presenter]:[string, any]) => {
				const currentShotHeight = presenter.currentShotResolution?.height || 0;
				const nextShotHeight = presenter.nextShotResolution?.height || 0;
				// const inNextShotButNotThisShot = !currentShotHeight && nextShotHeight;
				const preferredResolution = currentShotHeight > nextShotHeight
					? presenter.currentShotResolution
					: presenter.nextShotResolution;

				const subscriber = opentok?.subscribers[key];
				subscriber?.setPreferredResolution(preferredResolution);
			});
			keysToSetAsLowestResolution.forEach((key) => {
				const subscriber = opentok?.subscribers[key];
				const RESOLUTIONS = subscriber.stream?.videoDimensions?.height === 720
					? RESOLUTIONS_720 : RESOLUTIONS_1080;
				subscriber?.setPreferredResolution(RESOLUTIONS.LOW);
			});
		}
	}, [
		feedScaling,
		shots,
		connectedPresenterVideos,
		order,
		forcedLayout,
		shotFeeds,
		opentok?.subscribers,
	]);
};

export default useResolutionUpdate;
